import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {DescriptionIcon} from 'shells/icon';

import {Chevron} from './chevron';

import styles from './stage-chevron-container.module.css';

const COLUMN_WIDTH = 310;

type Props = {
    stage: {
        id: string;
        name: string;
        description: string;
        defaultConfidence: number | null | undefined;
    };
    isFirstChild: boolean;
    isLastChild: boolean;
    hasShadow?: boolean;
    includeDescriptionIcon?: boolean;
    includeConfidence?: boolean;
    onClick?: (stageId: string) => any;
    customStyles?: any;
    hoverStyle?: any;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

/*
 * Renders the a lead chevron for a given stage. This houses
 * the stage name and the lead pie associated with the stage.
 */
export class StageChevronContainer extends React.PureComponent<Props> {
    render() {
        return (
            <Chevron
                hasStraightLeft={this.props.isFirstChild}
                hasStraightRight={this.props.isLastChild}
                onMouseEnter={this.props.onMouseEnter}
                onMouseLeave={this.props.onMouseLeave}
                onClick={this.handleClick}
                style={{
                    width: this.getChevronWidth(),
                    ...this.props.customStyles,
                }}
                hasShadow={this.props.hasShadow}
                hoverStyle={this.getHoverStyle()}
            >
                <div className={styles.container}>
                    <div
                        title={
                            this.props.stage.name.length > 30 ? this.props.stage.name : undefined
                        }
                        className={styles['stage-name']}
                    >
                        {this.props.stage.name}
                        {this.props.includeDescriptionIcon && this.props.stage.description ? (
                            <DescriptionIcon className={styles['description-icon']} />
                        ) : undefined}
                    </div>
                    {this.props.includeConfidence && this.props.stage.defaultConfidence ? (
                        <div
                            title={`Leads that enter this stage will automatically have their confidence set to ${this.props.stage.defaultConfidence}%`}
                            className={styles['default-confidence']}
                        >
                            {`${this.props.stage.defaultConfidence}%`}
                        </div>
                    ) : undefined}
                </div>
            </Chevron>
        );
    }

    /**
     * Gets the width of the inner chevron container. This is unequal to the
     * COLUMN_WIDTH property, because this just represents the width of the
     * inner container, which does not include the trailing borders.
     *
     * @return {number}  - Width of the inner container
     */
    getChevronWidth = () => {
        // This account for the outside of the chevron's margin, such as the
        // left and right borders.
        const chevronAdditionalHorizontalMargin = 12;
        const innerContainerWidth = COLUMN_WIDTH - chevronAdditionalHorizontalMargin;

        // The first child needs to be wider because it doesn't have
        // any trailing chevron borders
        return this.props.isFirstChild ? innerContainerWidth + 6 : innerContainerWidth;
    };

    getHoverStyle = () => {
        if (this.props.hoverStyle) {
            return this.props.hoverStyle;
        }

        if (this.props.onClick) {
            return {backgroundColor: colors.listSelected, cursor: 'pointer'};
        }

        return undefined;
    };

    handleClick = () => {
        if (this.props.onClick) {
            this.props.onClick(this.props.stage.id);
        }
    };
}
